import { useState, useEffect } from 'react';
import { usersAPI } from '../utils/api';
import { useMasters } from '../hooks/useMasters';
import CustomFieldsEditor from './CustomFieldsEditor';

/**
 * frontend/src/components/ChangeRequestFormFields.jsx
 *
 * The change-request form, in one place.
 *
 * ChangeRequestsPage and DevHubPage each had their own: 9 fields against 12,
 * with only 5 in common. Dev Hub also sent `justification` and `priority`,
 * which the API translates to `business_justification` and `impact_level` for
 * backwards compatibility. This uses the CANONICAL names on both pages, so the
 * aliases stay supported for old callers but nothing new depends on them.
 *
 * Fields only — no modal chrome, no save button. The two pages keep their own
 * modal shells.
 *
 * Every value here is accepted by POST and by the PUT allowlist in
 * backend/src/routes/changeRequests.js.
 */

export const CR_REQUEST_TYPES = ['Change Request', 'New Feature', 'Enhancement', 'Support'];
export const CR_IMPACT_LEVELS = ['Low', 'Medium', 'High', 'Critical'];
export const CR_ENVIRONMENTS  = ['Development', 'UAT', 'Production'];
// Must match change_requests_billing_type_check exactly.
export const CR_BILLING_TYPES = [
  { v:'maintenance', l:'Maintenance (covered by contract)' },
  { v:'additional',  l:'Additional (billable)' },
  { v:'mixed',       l:'Mixed' },
  { v:'included',    l:'Included (no charge)' },
];
// Matches the approval flow used by the CR detail stepper.
export const CR_STATUSES = [
  'Pending', 'Under Review', 'Requirements Shared', 'BRD Signed Off',
  'Approved', 'In Development', 'Testing', 'Deployed', 'Implemented', 'Rejected',
];

export const emptyCRForm = () => ({
  custom_fields: {},
  requirements_shared_date: '', clarifications_date: '', requirements_received_date: '',
  proposal_shared_date: '', sign_off_date: '', deployment_uat_date: '', deployment_prod_date: '',
  estimated_hours_workflow: '', estimated_hours_offworkflow: '',
  estimated_hours_templates: '', estimated_hours_testing: '', billing_notes: '',
  project_id: '', title: '', description: '', business_justification: '',
  request_type: 'Change Request', impact_level: 'Medium', approval_status: 'Pending',
  environment: 'Development', implementation_date: '', department: '',
  authorized_requestor: '', lead_time_weeks: '', dev_time_weeks: '',
  billing_type: 'maintenance', assigned_to: '',
});

/** Load an existing CR into the form, tolerating the legacy alias names. */
export const crToForm = (cr) => ({
  ...emptyCRForm(),
  project_id:           cr?.project_id           ?? '',
  title:                cr?.title                ?? '',
  description:          cr?.description          ?? '',
  business_justification: cr?.business_justification ?? cr?.justification ?? '',
  request_type:         cr?.request_type         || 'Change Request',
  impact_level:         cr?.impact_level         || cr?.priority || 'Medium',
  approval_status:      cr?.approval_status      || 'Pending',
  environment:          cr?.environment          || 'Development',
  implementation_date:  (cr?.implementation_date || '').slice(0, 10),
  department:           cr?.department           ?? '',
  authorized_requestor: cr?.authorized_requestor ?? '',
  lead_time_weeks:      cr?.lead_time_weeks      ?? '',
  dev_time_weeks:       cr?.dev_time_weeks       ?? '',
  billing_type:         cr?.billing_type         || 'maintenance',
  assigned_to:          cr?.assigned_to          ?? '',
  custom_fields:        cr?.custom_fields ?? {},
});

const Field = ({ label, required, span, children }) => (
  <div className={span ? 'col-span-2' : ''}>
    <label className="label">{label}{required && ' *'}</label>
    {children}
  </div>
);

export default function ChangeRequestFormFields({
  form, setForm, projects = [], users: usersProp, me, showStatus = false,
}) {
  // Admin-controlled lists; the constants above are the fallback.
  const m = useMasters({
    requestType: ['cr_request_type', CR_REQUEST_TYPES],
    impact:      ['cr_impact',       CR_IMPACT_LEVELS],
    environment: ['bug_environment', CR_ENVIRONMENTS],
    billing:     ['billing_type',    CR_BILLING_TYPES.map(o => ({ value:o.v, label:o.l }))],
    status:      ['cr_status',       CR_STATUSES],
  });

  const [loaded, setLoaded] = useState([]);
  const users = usersProp?.length ? usersProp : loaded;
  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  // Only fetches when the page did not already supply the list.
  useEffect(() => {
    if (usersProp?.length) return;
    let cancelled = false;
    usersAPI.getAll()
      .then(r => { if (!cancelled) setLoaded(r.data || []); })
      .catch(() => { if (!cancelled) setLoaded([]); });
    return () => { cancelled = true; };
  }, [usersProp]);

  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="Project" required span>
        <select className="select" value={form.project_id || ''}
          onChange={e => set({ project_id: e.target.value })}>
          <option value="">Select project…</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>

      <Field label="Title" required span>
        <input className="input" value={form.title || ''}
          onChange={e => set({ title: e.target.value })}
          placeholder="Brief title for this change request" />
      </Field>

      <Field label="Request Type">
        <select className="select" value={form.request_type || 'Change Request'}
          onChange={e => set({ request_type: e.target.value })}>
          {m.requestType.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Impact Level">
        <select className="select" value={form.impact_level || 'Medium'}
          onChange={e => set({ impact_level: e.target.value })}>
          {m.impact.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>

      <Field label="Department">
        <input className="input" value={form.department || ''}
          onChange={e => set({ department: e.target.value })} placeholder="e.g. Credit Department" />
      </Field>
      <Field label="Authorized Requestor">
        <input className="input" value={form.authorized_requestor || ''}
          onChange={e => set({ authorized_requestor: e.target.value })} placeholder="Full name" />
      </Field>

      <Field label="Description of change" required span>
        <textarea className="input" rows={3} value={form.description || ''}
          onChange={e => set({ description: e.target.value })} placeholder="Describe the change in detail…" />
      </Field>
      <Field label="Justification / business case" span>
        <textarea className="input" rows={2} value={form.business_justification || ''}
          onChange={e => set({ business_justification: e.target.value })}
          placeholder="Why is this change needed?" />
      </Field>

      <Field label="Lead time (weeks)">
        <input type="number" min="0" step="0.5" className="input" value={form.lead_time_weeks || ''}
          onChange={e => set({ lead_time_weeks: e.target.value })} />
      </Field>
      <Field label="Dev time (weeks)">
        <input type="number" min="0" step="0.5" className="input" value={form.dev_time_weeks || ''}
          onChange={e => set({ dev_time_weeks: e.target.value })} />
      </Field>

      <Field label="Environment">
        <select className="select" value={form.environment || 'Development'}
          onChange={e => set({ environment: e.target.value })}>
          {m.environment.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Billing Type">
        <select className="select" value={form.billing_type || 'maintenance'}
          onChange={e => set({ billing_type: e.target.value })}>
          {m.billing.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>

      {showStatus && (
        <Field label="Approval Status">
          <select className="select" value={form.approval_status || 'Pending'}
            onChange={e => set({ approval_status: e.target.value })}>
            {m.status.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
      )}
      <Field label="Target implementation date">
        <input type="date" className="input" value={form.implementation_date || ''}
          onChange={e => set({ implementation_date: e.target.value })} />
      </Field>

      {/* Workflow dates. Three of these stamp themselves when the status
          first reaches the matching stage, and can be corrected here. The
          other four have no corresponding status, so they are recorded by
          hand — inferring them would be guessing at the process. */}
      <div className="col-span-2 pt-2 border-t" style={{ borderColor:'var(--border)' }}>
        <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color:'#2E539F' }}>Workflow dates</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div><label className="label">Requirements shared</label>
        <input type="date" className="input" value={(form.requirements_shared_date||'').slice(0,10)} onChange={e=>set({requirements_shared_date:e.target.value})}/>
        <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>Set automatically at that status</p>
      </div>
      <div><label className="label">Clarifications</label>
        <input type="date" className="input" value={(form.clarifications_date||'').slice(0,10)} onChange={e=>set({clarifications_date:e.target.value})}/>
      </div>
      <div><label className="label">Requirements received</label>
        <input type="date" className="input" value={(form.requirements_received_date||'').slice(0,10)} onChange={e=>set({requirements_received_date:e.target.value})}/>
      </div>
      <div><label className="label">Proposal shared</label>
        <input type="date" className="input" value={(form.proposal_shared_date||'').slice(0,10)} onChange={e=>set({proposal_shared_date:e.target.value})}/>
      </div>
      <div><label className="label">BRD signed off</label>
        <input type="date" className="input" value={(form.sign_off_date||'').slice(0,10)} onChange={e=>set({sign_off_date:e.target.value})}/>
        <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>Set automatically at that status</p>
      </div>
      <div><label className="label">Deployed to UAT</label>
        <input type="date" className="input" value={(form.deployment_uat_date||'').slice(0,10)} onChange={e=>set({deployment_uat_date:e.target.value})}/>
      </div>
      <div><label className="label">Deployed to production</label>
        <input type="date" className="input" value={(form.deployment_prod_date||'').slice(0,10)} onChange={e=>set({deployment_prod_date:e.target.value})}/>
        <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>Set automatically at that status</p>
      </div>
        </div>
      </div>

      {/* Effort breakdown. Four numeric columns the API has always accepted
          and no form has ever offered. */}
      <div className="col-span-2 pt-2 border-t" style={{ borderColor:'var(--border)' }}>
        <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color:'#2E539F' }}>Estimated hours</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div><label className="label">Workflow</label>
        <input type="number" min="0" step="0.25" className="input" value={form.estimated_hours_workflow ?? ''} onChange={e=>set({estimated_hours_workflow:e.target.value})}/></div>
      <div><label className="label">Off-workflow</label>
        <input type="number" min="0" step="0.25" className="input" value={form.estimated_hours_offworkflow ?? ''} onChange={e=>set({estimated_hours_offworkflow:e.target.value})}/></div>
      <div><label className="label">Templates</label>
        <input type="number" min="0" step="0.25" className="input" value={form.estimated_hours_templates ?? ''} onChange={e=>set({estimated_hours_templates:e.target.value})}/></div>
      <div><label className="label">Testing</label>
        <input type="number" min="0" step="0.25" className="input" value={form.estimated_hours_testing ?? ''} onChange={e=>set({estimated_hours_testing:e.target.value})}/></div>
        </div>
      </div>

      <div className="col-span-2"><label className="label">Billing notes</label>
        <textarea className="input" rows={2} value={form.billing_notes||''} onChange={e=>set({billing_notes:e.target.value})}/></div>

      {/* Admin-defined fields. Renders nothing until one is configured, so
          the form is unchanged for anyone who has not set any up. */}
      <div className="col-span-2">
        <CustomFieldsEditor appliesTo="change_request"
          values={form.custom_fields || {}}
          onChange={v => set({ custom_fields: v })} />
      </div>

      {/* Without an owner a CR gets approved and then nobody delivers it. */}
      <Field label="Assigned To" required span>
        <select className="select" value={form.assigned_to || ''}
          onChange={e => set({ assigned_to: e.target.value })}>
          <option value="">Select a person…</option>
          {users.filter(u => u.is_active).map(u => (
            <option key={u.id} value={u.id}>{u.name}{u.id === me?.id ? ' (me)' : ''}</option>
          ))}
        </select>
      </Field>
    </div>
  );
}

/** True when the form has everything the API requires. */
export const crFormValid = (f) =>
  !!(f?.project_id && f?.title?.trim() && f?.description?.trim() && f?.assigned_to);
